import ShadowPlane from '@/components/r3f-components/components/ShadowPlane';
import { ImageTracker } from '@zappar/zappar-react-three-fiber';
import { ISceneActions, ITransCatTypes, IUserActions, useActionStateStore, useDataStore, useSceneStore, useUserStore } from '@/zustand';
import { ReactElement, FunctionComponent, memo, useCallback, useEffect, useRef } from 'react';
import { Group } from 'three';
import { useGetTargetLostBehaviour, useGrabAndGoAnimation, useSubscribeToState } from './hooks';
import { IImageTrackingOrientation, IRoot, IShowArContentScreenRelativeAction, ITrackingTypes } from '@/components/r3f-components/component-data-structure';
import { useGetCurrentSceneId } from '@/hooks/general';
import { targetLostTimeout } from './ImageTrackerWrapper';
import { useGetActiveScene, useGetActiveTrackingType } from '@/components/hoc/hocCustomHooks';
import TrackingImageRepresentation from './TrackingImageRepresentation';
import * as Zappar from '@zappar/zappar-threejs';

interface IParentProps {
	children: ReactElement[] | ReactElement | undefined;
	trackingImageId: string;
	onVisible: (trackerId: string) => unknown;
	onNotVisible: (trackerId: string) => unknown;
	enabled: boolean;
}

const MAX_FRAMES = 5;

const ImageTrackerGroup: FunctionComponent<IParentProps> = ({ children, trackingImageId, onVisible, onNotVisible, enabled: isImageTrackerEnabled }) => {
	const sceneDispatch = useSceneStore(({ dispatch }) => dispatch);
	const userDispatch = useUserStore(({ dispatch }) => dispatch);
	const actionDispatch = useActionStateStore(({ dispatch }) => dispatch);
	const targetLostBehaviour = useGetTargetLostBehaviour();
	const imageTrackingDataDict = useDataStore((state) => state.trackingImageDict) ?? {};
	const currentScene = useGetActiveScene();
	const isTrackingImageVisible = useUserStore(({ isTrackingImageVisible }) => isTrackingImageVisible);
	const isGrabAndGoActive = useUserStore(({ isGrabNGoActive }) => isGrabNGoActive);
	const shadowsEnabled = useDataStore(({ shadowsEnabled }) => shadowsEnabled ?? true);
	const shadowObjectsGroupRef = useRef<Group>(null);
	const trackerContentGroupRef = useRef<Group>(null);
	const trackingImageRef = useRef<Group>(null);
	const trackerGroupRef = useRef<Group>(null);
	const currentSceneId = useGetCurrentSceneId();
	const { enterGrabAndGoBehaviour, exitGrabAndGoBehaviour } = useGrabAndGoAnimation(
		trackerContentGroupRef,
		trackerGroupRef,
		shadowObjectsGroupRef,
		trackingImageRef,
		imageTrackingDataDict[trackingImageId]?.orientation === IImageTrackingOrientation.flat
	);
	const framesAfterTargetlossRef = useRef(0);
	const activeTrackingType = useGetActiveTrackingType();
	const isImageTrackerEnabledRef = useSubscribeToState(isImageTrackerEnabled);

	const onFrameUpdateAfterTargetLoss = useCallback(() => {
		framesAfterTargetlossRef.current++;
		if (framesAfterTargetlossRef.current <= MAX_FRAMES) return;
		if (!trackerContentGroupRef.current) return;
		if (useUserStore.getState().isGrabNGoEnabled && isImageTrackerEnabledRef.current) {
			enterGrabAndGoBehaviour(() => {
				// console.log('SET GRAB AND GO ACTIVE')
				userDispatch({
					type: IUserActions.SET_GRAB_N_GO_ACTIVE,
					payload: true,
				});
				const { componentsById, rootId } = useDataStore.getState();
				const { activeSceneId } = useSceneStore.getState();
				const currSceneId = activeSceneId || (componentsById?.[rootId as string] as IRoot)?.children[0];
				sceneDispatch?.({
					type: ISceneActions.SET_TRANSITION_CATEGORY,
					transCatDict: { [currSceneId]: ITransCatTypes.enter },
				});
			});
		} else {
			userDispatch({
				type: IUserActions.SET_TRACKING_IMAGE_VISIBLE,
				payload: { visible: false },
			});
		}
		onNotVisible(trackingImageId);
		Zappar.onFrameUpdate.unbind(onFrameUpdateAfterTargetLoss);
	}, []);

	const imageTrackingData = imageTrackingDataDict[trackingImageId];
	const currentSceneTrackingId = currentScene?.trackingType === ITrackingTypes.image ? currentScene.trackingImageId : null;
	const isImageTrackerActive = trackingImageId === currentSceneTrackingId;
	const normalisationFactor = 2 / (imageTrackingData?.normalizationFactor || 1);

	useEffect(() => {
		if (activeTrackingType !== ITrackingTypes.image || !isGrabAndGoActive) exitGrabAndGoBehaviour();
	}, [activeTrackingType, isGrabAndGoActive]);

	// when target image is visible => hide target prompt
	// if first scene is image tracked and target visible for first time, call onTrackingReady

	// On tracking image recognition and after data is available:
	// - clear out target lost prompt timeout to avoid showing it
	// - if in grab & go mode, exit grab and go behaviour and set tracking image to visible
	// - flag target as been visible since enabling image tracking => this triggers a useEffect that triggers scene enter
	// - call onShowContentFirstTime function every time a tracking image is recognized
	// - set has been visible reference (used in initial show prompt logic)
	const _onVisible = useCallback(() => {
		Zappar.onFrameUpdate.unbind(onFrameUpdateAfterTargetLoss);
		framesAfterTargetlossRef.current = 0;
		clearTimeout(targetLostTimeout.value);
		onVisible(trackingImageId);
		exitGrabAndGoBehaviour();
		userDispatch({
			type: IUserActions.SET_TRACKING_IMAGE_VISIBLE,
			payload: { visible: true },
		});
	}, [isImageTrackerEnabled, userDispatch]);

	// When target image is lost:
	// - count frames since target image loss
	// - After max frames
	// 		- if grab and go mode & enabled => enter grab and go
	// 		- if not in grab & go mode or not enabled => set tracking image to not visible
	// - when target image is lost, show target prompt
	const _onNotVisible = useCallback(() => {
		// Clear up state
		Zappar.onFrameUpdate.unbind(onFrameUpdateAfterTargetLoss);
		framesAfterTargetlossRef.current = 0;
		Zappar.onFrameUpdate.bind(onFrameUpdateAfterTargetLoss);
	}, [userDispatch, isImageTrackerEnabled]);

	// if grab and go clicked
	// - enter scene (so the scene is visible) and interactive
	// - set tracking image to visible
	// - set hasBeenVisibleSinceEnabled to true => triggers enter of scene (again?!?)
	// - set grab n mode to true (stays true forever)
	// - set tracking image ref to visible
	useEffect(() => {
		if (!isGrabAndGoActive) return;
		if (!isImageTrackerActive) {
			if (trackingImageRef.current) trackingImageRef.current.visible = false;
			return;
		}
		enterGrabAndGoBehaviour(() => {
			userDispatch({
				type: IUserActions.SET_TRACKING_IMAGE_VISIBLE,
				payload: { visible: true },
			});
			sceneDispatch?.({
				type: ISceneActions.SET_TRANSITION_CATEGORY,
				transCatDict: { [currentSceneId]: ITransCatTypes.enter },
			});
		});
		if (trackingImageRef.current) trackingImageRef.current.visible = true;
	}, [isGrabAndGoActive, isImageTrackerActive]);

	return (
		<ImageTracker ref={trackerGroupRef as any} targetImage={imageTrackingData.file as unknown as string} onVisible={_onVisible} onNotVisible={_onNotVisible} enabled={false}>
			<group ref={trackerContentGroupRef} scale={typeof imageTrackingData?.topRadius == 'undefined' ? 1 : 1 / (normalisationFactor / 2)} visible={isTrackingImageVisible}>
				{children}
				<group visible={false} ref={trackingImageRef} scale={typeof imageTrackingData?.topRadius == 'undefined' ? 2 : 1}>
					{!!(targetLostBehaviour as IShowArContentScreenRelativeAction).showTrackingTarget && <TrackingImageRepresentation key={imageTrackingData.imageUrl} imageTrackingData={imageTrackingData} />}
				</group>
				<group ref={shadowObjectsGroupRef}>
					{/* <ShadowMesh shadowsEnabled={shadowsEnabled} imageTrackingData={imageTrackingData} /> */}
					{shadowsEnabled && <ShadowPlane opacity={0.3} />}
				</group>
			</group>
		</ImageTracker>
	);
};

ImageTrackerGroup.displayName = 'ImageTrackerGroup';
export default memo(ImageTrackerGroup);
